import { groupBy, omit } from 'lodash';

import { Currency } from '@work4all/models/lib/Classes/Currency.entity';
import { ErpPositionsKind } from '@work4all/models/lib/Enums/ErpPositionsKind.enum';

import { ErpObject, GroupedPosition } from '../../types';

export const groupByArticle = (data: ErpObject[]): GroupedPosition[] => {
  if (!data) return;

  const positionsWithArticles = data
    .flatMap((erpObject) =>
      (erpObject.positionList || []).map((pos) => ({
        ...pos,
        parent: erpObject,
      }))
    )
    .filter((pos) => pos.articleId)
    .filter((pos) => pos.positionKind !== ErpPositionsKind.STUECKLISTE);

  const groups = groupBy(positionsWithArticles, (item) => item.articleId);

  let incrementalPosId = 0;
  // Convert the grouped data into a flat array with headers and items
  const result = Object.entries(groups).flatMap(([_key, items]) => {
    const list: GroupedPosition[] = [
      {
        id: ++incrementalPosId,
        originalId: items[0]?.id,
        name: items[0]?.article?.name,
        longtext: items[0]?.article?.name,
        number: '',
        articleId: items[0]?.articleId,
        unit:
          'newUnit' in items[0]
            ? (items[0].newUnit as string)
            : items[0]?.article?.unit?.name,
        articleNumber: items[0]?.articleNumber,
        // When refreshing view we need to use changed value
        amount: items.reduce((p, c) => p + c.amount, 0),
        amountOriginal: items.reduce((p, c) => p + c.amount, 0),
        should: items.reduce((p, c) => p + c.amount, 0),
        singlePriceNet: items[0]?.singlePriceNet,
        discount:
          'newDiscount' in items[0]
            ? (items[0].newDiscount as number)
            : items[0]?.discount,
        // Header for the group
        positionKind:
          items.length > 1
            ? ErpPositionsKind.STUECKLISTE
            : ErpPositionsKind.STANDARD,
        supplier: items[0]?.supplier,
        supplierId: items[0]?.supplierId || 0,
        isHeader: true,
        childs: items.map((x) => x.id),
        selected:
          'selected' in items[0] ? (items[0].selected as boolean) : undefined,
        currency:
          'currency' in items[0]
            ? (items[0]?.currency as Currency)
            : items[0]?.parent?.currency,
      },
      // Items for the group
      ...(items.length > 1
        ? items.map((position) => ({
            ...omit(position, 'parent'),
            posId: incrementalPosId,
            singlePriceNet: position.purchasePrice,
            unit: position.article?.unit?.name,
            isGroupedChild: true,
            discount:
              'originalDiscount' in position
                ? (position.originalDiscount as number)
                : position.discount,
            supplierId: position.supplierId || 0,
            longtext: `${position.parent.contractNumber} | ${position.parent.businessPartnerContactCombined?.businessPartner.data.name}`,
            number: `Pos ${position.number}`,
            should: position.amount,
            articleId: position.articleId,
            selected:
              'selected' in position
                ? (position.selected as boolean)
                : undefined,
            currency:
              'currency' in position
                ? (position?.currency as Currency)
                : position.parent?.currency,
          }))
        : []),
    ];
    return list;
  });

  return result;
};
